'use client';

import { Mail, Phone } from 'lucide-react';

import { SmartLink, getLinkType } from '@/app/_components/core/smart-link';
import type { AccordionCardListCta } from '@/app/_components/shared/accordion-card-list/accordion-card-list.types';
import { LinkedinLogoIcon } from '@/generated/custom-icons';

type CtaType = NonNullable<AccordionCardListCta['type']>;

const CTA_ICON_MAP: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  phone: Phone,
  email: Mail,
  linkedin: LinkedinLogoIcon,
};

function resolveCtaType(cta: AccordionCardListCta): CtaType {
  if (cta.type) return cta.type;

  if (cta.url.startsWith('tel:')) return 'phone';
  if (cta.url.startsWith('mailto:')) return 'email';
  if (cta.url.includes('linkedin.com')) return 'linkedin';

  return 'link';
}

export function AccordionCardListCtas({
  ctas,
  trackingPrefix,
}: {
  ctas: AccordionCardListCta[];
  trackingPrefix?: string;
}) {
  if (ctas.length === 0) {
    return null;
  }

  return (
    <ul className="flex flex-col gap-1">
      {ctas.map((cta, index) => {
        const type = resolveCtaType(cta);
        const Icon = CTA_ICON_MAP[type];
        // CTAs here are auto-generated from CMS fields (LinkedIn etc.),
        // not editor-routed via SmartLink target. Force _blank for any
        // external URL so SmartLink emits rel="noopener noreferrer".
        const isExternal = getLinkType(cta.url) === 'external';

        return (
          <li key={cta.url}>
            <SmartLink
              href={cta.url}
              target={isExternal ? '_blank' : undefined}
              className="inline-flex items-center gap-1.5 text-sm text-primary underline hover:no-underline"
              data-track-id={
                trackingPrefix ? `${trackingPrefix}-cta-${index + 1}` : undefined
              }
            >
              <Icon className="size-4 shrink-0" />
              {cta.label}
            </SmartLink>
          </li>
        );
      })}
    </ul>
  );
}